import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, tareasService } from '../services/api';
import ConfirmPopup from '../components/ConfirmPopup';
import './MisGrupos.css';

const formatDateOnly = (dateString) => {
  if (!dateString) return '-';
  const s = String(dateString).trim().slice(0, 10);
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return dateString;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString('es-AR');
};

/** Agrupa tareas por curso; dentro del curso deduplica sets de grupos compartidos. */
function agruparPorCurso(tareas) {
  const cursosMap = new Map();

  for (const item of tareas || []) {
    const cursoId = item.curso_id;
    if (cursoId == null) continue;

    if (!cursosMap.has(cursoId)) {
      cursosMap.set(cursoId, {
        curso_id: cursoId,
        curso_nombre: item.curso_nombre || 'Curso',
        bloques: new Map(),
      });
    }
    const curso = cursosMap.get(cursoId);
    const fuenteId = item.grupos_fuente_tarea_id || item.id;

    if (!curso.bloques.has(fuenteId)) {
      curso.bloques.set(fuenteId, {
        fuente_id: fuenteId,
        tarea_join_id: item.id,
        titulos: [],
        fecha_cierre: item.fecha_cierre,
        grupos: item.grupos || [],
        mi_grupo_id: item.mi_grupo_id,
        grupos_cerrados: item.grupos_cerrados,
      });
    }

    const bloque = curso.bloques.get(fuenteId);
    if (item.titulo && !bloque.titulos.includes(item.titulo)) {
      bloque.titulos.push(item.titulo);
    }
    // Preferir fecha de cierre más próxima / la de la fuente si aplica
    if (item.id === fuenteId && item.fecha_cierre) {
      bloque.fecha_cierre = item.fecha_cierre;
    }
    if (item.mi_grupo_id) {
      bloque.mi_grupo_id = item.mi_grupo_id;
    }
    if (item.grupos_cerrados) {
      bloque.grupos_cerrados = true;
    }
  }

  return Array.from(cursosMap.values())
    .map((c) => ({
      ...c,
      bloques: Array.from(c.bloques.values()),
    }))
    .sort((a, b) => (a.curso_nombre || '').localeCompare(b.curso_nombre || '', 'es'));
}

const MisGrupos = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(null);
  const [expandedGrupo, setExpandedGrupo] = useState(null);
  const [grupoLlenoMsg, setGrupoLlenoMsg] = useState('');
  const [confirmSalir, setConfirmSalir] = useState(null);
  const [confirmCerrar, setConfirmCerrar] = useState(null);

  const cursos = useMemo(() => agruparPorCurso(list), [list]);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    setLoading(true);
    setError('');
    tareasService
      .getMisGruposTareas()
      .then((data) => setList(Array.isArray(data) ? data : []))
      .catch((err) => {
        setError(err.response?.data?.error || 'Error al cargar');
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const recargar = async () => {
    const data = await tareasService.getMisGruposTareas();
    setList(Array.isArray(data) ? data : []);
  };

  const handleUnirme = async (tareaId, grupoId) => {
    setJoining(`${tareaId}-${grupoId}`);
    setError('');
    try {
      await tareasService.unirmeAGrupo(tareaId, grupoId);
      await recargar();
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 || data.code === 'grupo_completo') {
        setGrupoLlenoMsg(
          data.error || 'El grupo se completó en este momento. Otro estudiante ocupó el último lugar.'
        );
        try { await recargar(); } catch (_) {}
      } else {
        setError(data.error || 'No se pudo unir al grupo');
        if (data.code === 'ingreso_cerrado') {
          try { await recargar(); } catch (_) {}
        }
      }
    } finally {
      setJoining(null);
    }
  };

  const handleSalir = async () => {
    if (!confirmSalir) return;
    const { tareaId, grupoId } = confirmSalir;
    setConfirmSalir(null);
    setJoining(`salir-${tareaId}-${grupoId}`);
    setError('');
    try {
      await tareasService.salirDeGrupo(tareaId, grupoId);
      await recargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo salir del grupo');
    } finally {
      setJoining(null);
    }
  };

  const handleCerrarIngreso = async () => {
    if (!confirmCerrar) return;
    const { tareaId, grupoId, cerrado } = confirmCerrar;
    setConfirmCerrar(null);
    setJoining(`cerrar-${tareaId}-${grupoId}`);
    setError('');
    try {
      await tareasService.cerrarIngresoGrupo(tareaId, grupoId, cerrado);
      await recargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo actualizar el ingreso del grupo');
    } finally {
      setJoining(null);
    }
  };

  if (loading) {
    return (
      <div className="mis-grupos-container">
        <div className="mis-grupos-loading">
          <div className="loading-spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mis-grupos-container">
      <header className="mis-grupos-header">
        <h1>Mis grupos</h1>
        <p className="mis-grupos-subtitle">
          Mientras la inscripción esté abierta podés unirte, salir o cerrar el ingreso de tu grupo.
        </p>
      </header>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <ConfirmPopup
        open={Boolean(grupoLlenoMsg)}
        title="Grupo completo"
        message={grupoLlenoMsg}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setGrupoLlenoMsg('')}
        onCancel={() => setGrupoLlenoMsg('')}
        variant="primary"
      />
      <ConfirmPopup
        open={Boolean(confirmSalir)}
        title="Salir del grupo"
        message={confirmSalir ? `¿Salir de ${confirmSalir.nombre}? Vas a quedar sin grupo hasta que elijas otro.` : ''}
        confirmLabel="Salir"
        cancelLabel="Cancelar"
        onConfirm={handleSalir}
        onCancel={() => setConfirmSalir(null)}
        variant="danger"
      />
      <ConfirmPopup
        open={Boolean(confirmCerrar)}
        title={confirmCerrar?.cerrado ? 'Cerrar ingreso' : 'Reabrir ingreso'}
        message={
          confirmCerrar?.cerrado
            ? `Nadie más va a poder unirse a ${confirmCerrar.nombre}, aunque todavía haya lugar.`
            : `¿Reabrir el ingreso a ${confirmCerrar?.nombre || 'este grupo'}?`
        }
        confirmLabel={confirmCerrar?.cerrado ? 'Cerrar ingreso' : 'Reabrir'}
        cancelLabel="Cancelar"
        onConfirm={handleCerrarIngreso}
        onCancel={() => setConfirmCerrar(null)}
        variant={confirmCerrar?.cerrado ? 'danger' : 'primary'}
      />

      {cursos.length === 0 ? (
        <p className="mis-grupos-empty">
          No tenés grupos disponibles en cursos habilitados, o aún no se crearon grupos para esas tareas.
        </p>
      ) : (
        <ul className="mis-grupos-list">
          {cursos.map((curso) => (
            <li key={curso.curso_id} className="mis-grupos-card">
              <div className="mis-grupos-card-header">
                <h2 className="mis-grupos-card-titulo">{curso.curso_nombre}</h2>
                {curso.bloques.length > 1 && (
                  <span className="mis-grupos-card-meta">
                    {curso.bloques.length} conjuntos de grupos en este curso
                  </span>
                )}
              </div>

              <div className="mis-grupos-card-body">
                {curso.bloques.map((bloque) => {
                  const tituloBloque = bloque.titulos.length > 1
                    ? bloque.titulos.join(' · ')
                    : (bloque.titulos[0] || 'Grupos');
                  return (
                    <section key={bloque.fuente_id} className="mis-grupos-bloque">
                      <div className="mis-grupos-bloque-header">
                        <h3 className="mis-grupos-bloque-titulo">{tituloBloque}</h3>
                        {bloque.fecha_cierre && (
                          <span className="mis-grupos-card-fecha">
                            Cierre: {formatDateOnly(bloque.fecha_cierre)}
                          </span>
                        )}
                      </div>

                      {bloque.grupos_cerrados && !bloque.mi_grupo_id && (
                        <p className="mis-grupos-inscripcion-cerrada">
                          La inscripción a grupos está cerrada. Si necesitás unirte, consultá con tu docente.
                        </p>
                      )}
                      {bloque.mi_grupo_id ? (
                        <p className="mis-grupos-ya-estoy">
                          Estás en:{' '}
                          <strong>
                            {bloque.grupos?.find((g) => g.id === bloque.mi_grupo_id)?.nombre || 'Grupo'}
                          </strong>
                        </p>
                      ) : !bloque.grupos_cerrados ? (
                        <p className="mis-grupos-elegi">Elegí un grupo para unirte:</p>
                      ) : null}

                      <div className="mis-grupos-grupos">
                        {(bloque.grupos || []).map((g) => {
                          const soyYo = g.id === bloque.mi_grupo_id;
                          const completo = g.completo === true;
                          const ingresoCerrado = g.ingreso_cerrado === true;
                          const inscripcionAbierta = !bloque.grupos_cerrados;
                          const puedeUnirse = inscripcionAbierta && !bloque.mi_grupo_id && !completo && !ingresoCerrado;
                          const accionKey = `${bloque.tarea_join_id}-${g.id}`;
                          const expandKey = `${curso.curso_id}-${bloque.fuente_id}-${g.id}`;
                          const isExpanded = expandedGrupo === expandKey;
                          const integrantes = g.integrantes || [];
                          return (
                            <div key={g.id} className="mis-grupos-grupo-wrap">
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setExpandedGrupo(isExpanded ? null : expandKey)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setExpandedGrupo(isExpanded ? null : expandKey);
                                  }
                                }}
                                className={`mis-grupos-grupo ${soyYo ? 'mis-grupos-grupo--yo' : ''} ${completo ? 'mis-grupos-grupo--completo' : ''} ${ingresoCerrado ? 'mis-grupos-grupo--ingreso-cerrado' : ''} ${isExpanded ? 'mis-grupos-grupo--expanded' : ''}`}
                                aria-expanded={isExpanded}
                                aria-label={`${g.nombre}, ${g.cantidad} integrantes. ${isExpanded ? 'Ocultar' : 'Ver'} integrantes`}
                              >
                                <span className="mis-grupos-grupo-nombre">{g.nombre}</span>
                                <span className="mis-grupos-grupo-count">
                                  {g.max_integrantes != null
                                    ? `${g.cantidad} / ${g.max_integrantes}`
                                    : `${g.cantidad} integrantes`}
                                </span>
                                {soyYo && <span className="mis-grupos-grupo-badge">Estoy aquí</span>}
                                {soyYo && ingresoCerrado && inscripcionAbierta && (
                                  <span className="mis-grupos-grupo-cerrado">Ingreso cerrado</span>
                                )}
                                {puedeUnirse && (
                                  <button
                                    type="button"
                                    className="mis-grupos-btn-unirme"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUnirme(bloque.tarea_join_id, g.id);
                                    }}
                                    disabled={joining !== null}
                                  >
                                    {joining === accionKey ? 'Uniendo…' : 'Unirme'}
                                  </button>
                                )}
                                {soyYo && inscripcionAbierta && (
                                  <>
                                    <button
                                      type="button"
                                      className="mis-grupos-btn-salir"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmSalir({
                                          tareaId: bloque.tarea_join_id,
                                          grupoId: g.id,
                                          nombre: g.nombre,
                                        });
                                      }}
                                      disabled={joining !== null}
                                    >
                                      {joining === `salir-${accionKey}` ? 'Saliendo…' : 'Salir'}
                                    </button>
                                    <button
                                      type="button"
                                      className="mis-grupos-btn-cerrar"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmCerrar({
                                          tareaId: bloque.tarea_join_id,
                                          grupoId: g.id,
                                          nombre: g.nombre,
                                          cerrado: !ingresoCerrado,
                                        });
                                      }}
                                      disabled={joining !== null}
                                    >
                                      {joining === `cerrar-${accionKey}`
                                        ? 'Guardando…'
                                        : (ingresoCerrado ? 'Reabrir ingreso' : 'Cerrar ingreso')}
                                    </button>
                                  </>
                                )}
                                {completo && !soyYo && inscripcionAbierta && (
                                  <span className="mis-grupos-grupo-completo">Completo</span>
                                )}
                                {ingresoCerrado && !completo && !soyYo && inscripcionAbierta && (
                                  <span className="mis-grupos-grupo-cerrado">Ingreso cerrado</span>
                                )}
                                {bloque.grupos_cerrados && !soyYo && !bloque.mi_grupo_id && (
                                  <span className="mis-grupos-grupo-cerrado">Cerrado</span>
                                )}
                                <span className="mis-grupos-grupo-chevron" aria-hidden>
                                  {isExpanded ? '▼' : '▶'}
                                </span>
                              </div>
                              {isExpanded && (
                                <div className="mis-grupos-integrantes">
                                  <p className="mis-grupos-integrantes-title">Integrantes</p>
                                  <ul className="mis-grupos-integrantes-list">
                                    {integrantes.length === 0 ? (
                                      <li className="mis-grupos-integrantes-empty">Ninguno aún</li>
                                    ) : (
                                      integrantes.map((int) => (
                                        <li key={int.id}>{int.nombre || '—'}</li>
                                      ))
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MisGrupos;
